import { Check } from "@mui/icons-material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { type ChangeEvent, type KeyboardEvent, useEffect, useState } from "react";
import { ImpactFilterMenu } from "@/components/impactReport/ImpactFilterMenu";
import {
  IMPACT_REPORT_DATE_CUSTOM_APPLY_LABEL,
  IMPACT_REPORT_DATE_CUSTOM_FROM_LABEL,
  IMPACT_REPORT_DATE_CUSTOM_INCOMPLETE_TEXT,
  IMPACT_REPORT_DATE_CUSTOM_TO_LABEL,
  IMPACT_REPORT_DATE_PRESET_ENTRIES,
} from "@/constants/impactReport";
import type { DatePresetMenuProps } from "@/interfaces/datePresetMenu";
import type { ImpactDatePreset } from "@/interfaces/impactReport";
import { resolveImpactDateWindow } from "@/utils/impact-report-filter-helper";

/**
 * The date control: task 1's presets, plus a custom range that opens INSIDE the menu.
 *
 * Takes the selection through props rather than reading it from a context, because more than one
 * screen now has a date selection of its own. Each owner keeps its selection where it already
 * lives, and this stays the one place the presets and the custom-range form are spelled out.
 *
 * The menu stays open while the custom inputs are being filled — a menu that closed on the first
 * click would make a two-field range impossible to enter. Only a preset, or Apply, commits.
 *
 * An INCOMPLETE custom range falls back to all time rather than sending a half-open window: a
 * window open at one end silently asks a far wider question than the user was in the middle of
 * asking. That fallback is not re-derived here — task 1's `resolveImpactDateWindow` already
 * answers `null` for it, and this component simply commits what was typed and lets the composer
 * omit the date keys.
 */
export function DatePresetMenu({
  value,
  onChange,
  renderTrigger,
  label,
  icon,
  buttonAriaLabel,
  menuMinWidth,
  triggerButtonRef,
}: DatePresetMenuProps) {
  const date = value;

  // DRAFT state, uncommitted. Kept out of the caller's selection on purpose: a half-typed range
  // must not reach the filter, and therefore must not reach the query key.
  const [showCustomRange, setShowCustomRange] = useState(date.preset === "custom");
  const [customFrom, setCustomFrom] = useState(date.customFrom ?? "");
  const [customTo, setCustomTo] = useState(date.customTo ?? "");

  // Re-seed the drafts whenever the COMMITTED selection changes underneath this component — a
  // clear-all elsewhere in the bar has to empty these inputs too, or the menu would keep showing
  // a range the report is no longer built from. Typing does not commit, so this never fights the
  // user mid-entry.
  useEffect(() => {
    setShowCustomRange(date.preset === "custom");
    setCustomFrom(date.customFrom ?? "");
    setCustomTo(date.customTo ?? "");
  }, [date.preset, date.customFrom, date.customTo]);

  // "Is this control at a non-default value?" answered by the same function that decides what goes
  // on the wire, so the lit button and the outgoing request can never disagree — in particular a
  // committed-but-incomplete custom range sends no dates and correctly reads as inactive. A bare
  // `new Date()` is enough: this is a boolean for a border colour, not a composed filter.
  const isActive = resolveImpactDateWindow(date, new Date()) !== null;

  const isCustomRangeIncomplete = customFrom === "" || customTo === "";

  function pickPreset(preset: ImpactDatePreset, closeMenu: () => void) {
    // "Custom range…" opens the sub-form and commits NOTHING — the user has not chosen a window
    // yet, and committing here would blank the report the moment they went looking for one.
    if (preset === "custom") {
      setShowCustomRange(true);
      return;
    }
    setShowCustomRange(false);
    onChange({ preset });
    closeMenu();
  }

  function applyCustomRange(closeMenu: () => void) {
    onChange({ preset: "custom", customFrom, customTo });
    closeMenu();
  }

  return (
    <ImpactFilterMenu
      label={label}
      icon={icon}
      isActive={isActive}
      buttonAriaLabel={buttonAriaLabel}
      menuMinWidth={menuMinWidth}
      triggerButtonRef={triggerButtonRef}
      // `ImpactFilterMenu` ignores `label`/`icon` whenever `renderTrigger` is present, so it is
      // forwarded only when a caller actually supplied one.
      {...(renderTrigger ? { renderTrigger } : {})}
    >
      {(closeMenu) => [
        ...IMPACT_REPORT_DATE_PRESET_ENTRIES.map(([preset, label]) => {
          const isSelected =
            preset === "custom" ? showCustomRange : date.preset === preset && !showCustomRange;
          return (
            <MenuItem
              key={preset}
              selected={isSelected}
              onClick={() => pickPreset(preset, closeMenu)}
              sx={{ fontSize: 13.5, gap: 1.25 }}
            >
              <Box component="span" sx={{ flex: 1 }}>
                {label}
              </Box>
              <Check
                sx={{ fontSize: 16, color: isSelected ? "var(--color-dark-dust)" : "transparent" }}
              />
            </MenuItem>
          );
        }),
        showCustomRange ? <Divider key="custom-range-divider" sx={{ my: "5px" }} /> : null,
        showCustomRange ? (
          <Box key="custom-range" sx={{ px: "12px", pt: "4px", pb: "10px" }}>
            <Box sx={{ display: "flex", gap: "10px" }}>
              <CustomDateField
                label={IMPACT_REPORT_DATE_CUSTOM_FROM_LABEL}
                value={customFrom}
                onChange={setCustomFrom}
              />
              <CustomDateField
                label={IMPACT_REPORT_DATE_CUSTOM_TO_LABEL}
                value={customTo}
                onChange={setCustomTo}
              />
            </Box>
            {isCustomRangeIncomplete && (
              <Typography
                sx={{
                  mt: "8px",
                  fontSize: "11.5px",
                  lineHeight: 1.4,
                  color: "var(--color-graphite)",
                }}
              >
                {IMPACT_REPORT_DATE_CUSTOM_INCOMPLETE_TEXT}
              </Typography>
            )}
            <Box
              component="button"
              type="button"
              onClick={() => applyCustomRange(closeMenu)}
              sx={{
                width: "100%",
                mt: "12px",
                height: "38px",
                borderRadius: "8px",
                border: "none",
                bgcolor: "var(--color-rodeo-black)",
                color: "var(--color-white)",
                fontFamily: "var(--font-lato)",
                fontSize: "13px",
                fontWeight: 700,
                letterSpacing: "0.5px",
                cursor: "pointer",
                "&:hover": { bgcolor: "#000000" },
              }}
            >
              {IMPACT_REPORT_DATE_CUSTOM_APPLY_LABEL}
            </Box>
          </Box>
        ) : null,
      ]}
    </ImpactFilterMenu>
  );
}

interface CustomDateFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

/** One labelled `YYYY-MM-DD` input — the prototype's `.ir-date` in this repo's idiom. */
function CustomDateField({ label, value, onChange }: CustomDateFieldProps) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ mb: "4px", fontSize: "11px", color: "var(--color-graphite)" }}>
        {label}
      </Typography>
      <Box
        component="input"
        type="date"
        value={value}
        aria-label={label}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        // MUI's `MenuList` treats a printable keypress as type-ahead and moves focus to the
        // matching item — which would yank focus out of this input mid-entry. The keystrokes
        // belong to the field, so they stop here.
        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => event.stopPropagation()}
        sx={{
          width: "100%",
          height: "38px",
          px: "10px",
          boxSizing: "border-box",
          border: "1px solid var(--color-smoke)",
          borderRadius: "8px",
          bgcolor: "var(--color-white)",
          fontFamily: "var(--font-lato)",
          fontSize: "13px",
          color: "var(--color-rodeo-black)",
          "&:focus": { outline: "none", borderColor: "var(--color-dark-dust)" },
        }}
      />
    </Box>
  );
}
